import React, { useEffect, useState } from "react"
import "./infoCv.scss"
import { Grid } from "@mui/material"
import { useQuery } from "react-query"
import ThemeCvDto from "../../../../../api/models/cvTheme"
import cvskillService from "../../../../../api/services/cvskillService"
import { useCvSkill } from "../../../../../hooks/useCvSkill"

interface InfoCvProps {
	isPhoto: boolean
	theme: ThemeCvDto | null
}

function InfoCv({ isPhoto, theme }: InfoCvProps) {
	const { cvSkill, loading, error } = useCvSkill()
	const [themeCv, setThemeCv] = useState(theme)
	// Start empty so placeholder isn't actually printed
	const [text, setText] = useState("")

	const user = cvSkill?.user

	// Synchroniser le thème
	useEffect(() => {
		setThemeCv(theme)
	}, [theme])

	// Récupération de la photo
	const { data: photoData } = useQuery(
		["photo", cvSkill?.id],
		() => (cvSkill?.id ? cvskillService.getPhoto(cvSkill.id) : null),
		{ enabled: !!cvSkill?.id },
	)

	const photoUrl = photoData
		? URL.createObjectURL(new Blob([photoData], { type: "image/jpeg" }))
		: null

	// Nettoyage de l'URL de la photo
	useEffect(
		() => () => {
			if (photoUrl) URL.revokeObjectURL(photoUrl)
		},
		[photoUrl],
	)

	const calculeAge = (dateValue: string | Date): number => {
		const birthDate =
			dateValue instanceof Date ? dateValue : new Date(dateValue)
		const today = new Date()

		let age = today.getFullYear() - birthDate.getFullYear()
		const monthDifference = today.getMonth() - birthDate.getMonth()

		if (
			monthDifference < 0 ||
			(monthDifference === 0 && today.getDate() < birthDate.getDate())
		) {
			age -= 1
		}

		return age
	}

	// Styles
	const styleBloc: React.CSSProperties = {
		borderRight: `2px solid ${themeCv?.colorBorder}`,
		borderBottom: `2px solid ${themeCv?.colorBorder}`,
		padding: "30px",
		display: "inline-block",
		backgroundColor: `${themeCv?.infoColorBackground}`,
		minHeight: "820px",
		backgroundImage: `url("${themeCv?.infoImgBackground}")`,
		backgroundSize: "contain",
		backgroundRepeat: "no-repeat",
		backgroundPositionX: "right",
		backgroundPositionY: "top",
		fontFamily: `"${themeCv?.fontFamily}", "sans-serif"`,
		position: "relative",
	}

	const styleNom = {
		color: `${themeCv?.infoColorName}`,
		fontSize: "60px",
		marginTop: "60px",
	}

	const stylePrenom = {
		color: `${themeCv?.infoColorName}`,
		fontSize: "60px",
		marginTop: "5px",
	}

	const styleInfo = {
		color: `${themeCv?.infoColorText}`,
		fontSize: "30px",
		marginTop: "15px",
	}

	const styleTextArea = {
		color: `${theme?.intColorText}`,
		backgroundColor: "transparent",
		marginTop: "15px",
		// Rétrécie pour laisser la place à la photo, sinon prend toute la largeur disponible
		width: photoUrl && isPhoto ? "145px" : "500px",
		height: "420px",
		padding: "10px",
		fontSize: "16px",
		borderRadius: "5px",
		border: "0px solid #ccc",
		resize: "none" as "none",
	}

	// ✨ DESIGN POUR IMPRESSION - Photo ronde en bas à droite, grande et professionnelle
	const stylePhotoModerne: React.CSSProperties = {
		position: "absolute",
		bottom: "70px", // Position en hauteur
		right: "90px", // Légèrement plus à droite (était 120px)
		width: "300px", // Encore plus grande (était 250px)
		height: "300px",
		objectFit: "cover",
		borderRadius: "50%", // Complètement rond
		border: `5px solid white`, // Bordure blanche épaisse
		boxShadow: `
			0 8px 20px rgba(0, 0, 0, 0.15),
			0 0 0 3px ${themeCv?.infoColorBackground || "#2daf8e"}
		`, // Ombre simple + bordure couleur thème
		zIndex: 15,
	}

	// ✅ Style pour l'étoile au milieu (TOUJOURS visible)
	// Centrée sur le point de jonction des 4 cases (coin bas-droit de ce panneau)
	const styleEtoileAuMilieu: React.CSSProperties = {
		position: "absolute",
		bottom: 0,
		right: 0,
		transform: "translate(50%, 50%)",
		width: "38%",
		zIndex: 20,
	}

	// Gestion des états de chargement et d'erreur
	if (loading) {
		return (
			<Grid size={6} style={styleBloc} sx={{ pt: 5 }}>
				<p>Chargement des informations...</p>
			</Grid>
		)
	}

	if (error) {
		return (
			<Grid size={6} style={styleBloc} sx={{ pt: 5 }}>
				<p style={{ color: "red" }}>{error}</p>
			</Grid>
		)
	}

	return (
		<Grid size={6} style={styleBloc} sx={{ pt: 5 }}>
			{/* ✨ Photo moderne en bas à droite - occupe l'espace disponible SI photo existe ET isPhoto=true */}
			{photoUrl && isPhoto && (
				// eslint-disable-next-line jsx-a11y/img-redundant-alt
				<img
					src={photoUrl}
					alt="Photo de profil"
					style={stylePhotoModerne}
					className="photo-moderne"
				/>
			)}

			{user && (
				<>
					<h1 style={styleNom}>{user.name}</h1>
					<h1 style={stylePrenom}>{user.firstName}</h1>
					{user.dateOfBirth && (
						<h3 style={styleInfo}>
							{calculeAge(user.dateOfBirth)} ans
						</h3>
					)}
					<h3 style={styleInfo}>{user.phone}</h3>
					<h3 style={styleInfo}>{user.email}</h3>
					{user.diploma && <h3 style={styleInfo}>{user.diploma}</h3>}

					<textarea
						style={styleTextArea}
						value={text}
						onChange={e => setText(e.target.value)}
						placeholder="Exprimez vous...!"
						className={
							text.trim() === ""
								? "empty-for-print"
								: "filled-for-print"
						}
					/>
				</>
			)}

			{/* ✅ Étoile au milieu - TOUJOURS visible */}
			{themeCv && (
				<img
					src={themeCv.imagePrincipale}
					alt={themeCv.name}
					style={styleEtoileAuMilieu}
					className="not-img-profil"
				/>
			)}
		</Grid>
	)
}

export default InfoCv
